
import React, { useState } from 'react';
import { SectionWrapper } from './SectionWrapper';
import { STUDIO_PHOTOS } from '../constants';
import { Photo } from '../types';
import { X, MapPin } from 'lucide-react';

export const Space: React.FC = () => {
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [isDeveloping, setIsDeveloping] = useState(false);

  const handlePhotoClick = (photo: Photo) => {
    setSelectedPhoto(photo);
    setIsDeveloping(true);
    setTimeout(() => setIsDeveloping(false), 2500);
  };

  return (
    <>
      <SectionWrapper>
        {/* 统一头部：空间 */}
        <div className="text-center mb-16 md:mb-28 animate-fade-in flex flex-col items-center">
          {/* 
             调整1：主标题
             - text-3xl md:text-4xl (与咨询手记保持一致)
             - text-stone-800 (柔和深灰)
             - font-bold (去除极粗带来的压迫感)
          */}
          <h2 className="font-serif text-3xl md:text-4xl text-stone-800 tracking-widest mb-4 font-bold">
            工作空间
          </h2>
          <div className="flex flex-col items-center gap-2">
            <span className="text-stone-400 font-sans uppercase text-xs tracking-[0.6em] font-bold">The Frame</span>
            {/* 
               调整2：引言
               - font-normal (增加呼吸感)
            */}
            <p className="text-stone-600 font-serif text-xl mt-10 max-w-xl leading-loose italic font-normal">
              “咨询室可能是让你「躲起来」又「被看见」的空间。”
            </p>
          </div>
        </div>

        <div className="relative animate-fade-in">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 py-4 md:py-10">
             {STUDIO_PHOTOS.map((photo, index) => (
               <div 
                 key={index}
                 onClick={() => handlePhotoClick(photo)}
                 className="group relative cursor-pointer transform transition-all duration-500 hover:scale-105 hover:z-10"
                 style={{ transform: `rotate(${photo.rotation})` }}
               >
                  {/* 胶带效果 */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-12 h-4 bg-stone-100/50 backdrop-blur-[1px] rounded-[1px] z-20 shadow-[0_1px_2px_rgba(0,0,0,0.05)] opacity-80"></div>
                  
                  {/* 照片卡片本体 */}
                  <div className="bg-white p-3 pb-8 md:p-4 md:pb-10 shadow-[0_4px_15px_rgba(0,0,0,0.05)] rounded-[2px] transition-shadow duration-500 group-hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.1)]">
                     <div className="w-40 h-40 md:w-60 md:h-60 overflow-hidden bg-stone-100 relative filter brightness-[1.02] contrast-[0.98]">
                        <img 
                          src={photo.url} 
                          alt={photo.title} 
                          className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
                        />
                        {/* 图片上的遮罩引导 */}
                        <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/5 transition-colors duration-300"></div>
                     </div>
                     
                     {/* 
                        调整3：卡片标题
                        - text-lg md:text-xl (缩小约 20%)
                     */}
                     <div className="mt-4 md:mt-5 text-center relative px-2">
                        <h3 className="font-serif text-lg md:text-xl text-stone-800 font-bold transform -rotate-1 group-hover:text-stone-900 group-hover:rotate-0 transition-all duration-300 select-none tracking-wide">
                          {photo.title}
                        </h3>
                        
                        {/* 点击提示 */}
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 absolute -bottom-5 left-0 right-0 flex justify-center items-center gap-1">
                           <span className="text-[8px] font-sans tracking-widest text-stone-300 uppercase scale-90">View Detail</span>
                        </div>
                     </div>
                  </div>
               </div>
             ))}
          </div>

          {/* 地址展示区块 */}
          <div className="mt-24 md:mt-32 flex flex-col items-center text-center animate-fade-in border-t border-dashed border-stone-200 pt-16">
             <div className="flex items-center gap-2 text-stone-400 mb-4 bg-white/50 px-4 py-1.5 rounded-full border border-stone-100">
                <MapPin size={16} className="text-rose-300" />
                <span className="text-[10px] tracking-[0.3em] font-mono uppercase font-black">Studio Location</span>
             </div>
             <p className="font-serif text-stone-800 text-xl md:text-2xl tracking-widest leading-relaxed font-bold">
               成都市 · 成华区 · 萌想星球107文创园
               <br />
               <span className="text-stone-600 mt-2 block font-normal text-lg">6号楼 4楼 415号</span>
             </p>
          </div>
        </div>
      </SectionWrapper>

      {/* 将弹窗移至 SectionWrapper 外部，确保 fixed 定位生效 */}
      {selectedPhoto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md animate-fade-in p-4" onClick={() => setSelectedPhoto(null)}>
           <div className="relative max-w-4xl w-full flex flex-col items-center" onClick={e => e.stopPropagation()}>
             <button onClick={() => setSelectedPhoto(null)} className="absolute top-[-50px] right-0 text-white/70 hover:text-white transition-colors"><X size={32} /></button>
             <div className="bg-white p-4 pb-16 md:p-5 md:pb-20 shadow-2xl transform rotate-1 rounded-[2px]">
                <div className="relative bg-stone-200 aspect-[4/3] min-w-[300px] md:min-w-[700px]">
                   <img src={selectedPhoto.url} alt={selectedPhoto.title} className={`w-full h-full object-cover ${isDeveloping ? 'animate-develop' : ''}`} />
                </div>
                <div className="mt-8 text-center px-6">
                   {/* 弹窗中的标题也同步改为 Serif */}
                   <p className="font-serif text-3xl md:text-4xl text-stone-800 font-bold mb-3">{selectedPhoto.title}</p>
                   <p className="text-stone-600 font-serif italic text-base md:text-lg opacity-80">{selectedPhoto.description}</p>
                </div>
             </div>
           </div>
        </div>
      )}
    </>
  );
};
